import { a as __assign } from './tslib.es6-a39f91fc.js';
import { jsxs, jsx } from 'react/jsx-runtime';
import { c as classNames } from './index-6ea95111.js';
import { s as styleInject } from './style-inject.es-1f59c1d0.js';
import { TableCell } from './components/table/components/TableCell/TableCell.js';
import { useState, useEffect, useCallback } from 'react';

var css_248z = ".TableSection-module_table__sNbDq {\n  border-collapse: collapse;\n  table-layout: fixed;\n}\n@media (min-width: 34rem) {\n  .TableSection-module_table__sNbDq {\n    min-width: 845px;\n  }\n}\n\n.TableSection-module_tr__UbkbE {\n  min-height: 72px;\n}";
var styles = {"table":"TableSection-module_table__sNbDq","tr":"TableSection-module_tr__UbkbE"};
styleInject(css_248z);

var isBaseCell = function (tableCellData) {
    return !tableCellData.type;
};

var breakpointLookup = function (breakpoint) {
    if (typeof window === 'undefined') {
        return {
            initialValue: false,
            queryString: '',
        };
    }
    switch (breakpoint) {
        case 'BELOW_TABLET':
            return {
                initialValue: window.innerWidth <= 720, // 45rem = 720px = tablet breakpoint
                queryString: '(max-width: 45rem)',
            };
        case 'BELOW_DESKTOP':
            return {
                initialValue: window.innerWidth <= 1024, // 64rem = 1024px = desktop breakpoint
                queryString: '(max-width: 64rem)',
            };
        case 'ABOVE_MOBILE':
            return {
                initialValue: window.innerWidth >= 544, // 34rem = 544px = mobile breakpoint}
                queryString: '(min-width: 34rem)',
            };
        case 'ABOVE_TABLET':
            return {
                initialValue: window.innerWidth >= 720, // 45rem = 720px = tablet breakpoint
                queryString: '(min-width: 45rem)',
            };
        case 'ABOVE_DESKTOP':
            return {
                initialValue: window.innerWidth >= 1024, // 64rem = 1024px = desktop breakpoint
                queryString: '(min-width: 64rem)',
            };
        case 'BELOW_MOBILE':
        default:
            return {
                initialValue: window.innerWidth <= 544, // 34rem = 544px = mobile breakpoint}
                queryString: '(max-width: 34rem)',
            };
    }
};
var useMediaQuery = function (breakpoint) {
    var _a = breakpointLookup(breakpoint), initialValue = _a.initialValue, queryString = _a.queryString;
    var _b = useState(initialValue), matchesBreakpoint = _b[0], setMatchesBreakpoint = _b[1];
    useEffect(function () {
        var mediaQuery = window.matchMedia(queryString);
        var updateMediaQuery = function (e) {
            return setMatchesBreakpoint(e.matches);
        };
        mediaQuery.addEventListener('change', updateMediaQuery);
        return function () {
            mediaQuery.removeEventListener('change', updateMediaQuery);
        };
    }, [queryString]);
    return matchesBreakpoint;
};

var TableSection = function (_a) {
    var className = _a.className, tableCellRows = _a.tableCellRows, _b = _a.hideColumns, hideColumns = _b === void 0 ? [] : _b, hideHeader = _a.hideHeader, openModal = _a.openModal, title = _a.title, width = _a.width, cellReplacements = _a.cellReplacements, imageComponent = _a.imageComponent;
    var headerRow = tableCellRows === null || tableCellRows === void 0 ? void 0 : tableCellRows[0];
    var isBelowDesktop = useMediaQuery('BELOW_DESKTOP');
    var getModalTitleFromColumnHeader = function (cellIndex) {
        var _a;
        var firstCellInColumn = (_a = tableCellRows === null || tableCellRows === void 0 ? void 0 : tableCellRows[0]) === null || _a === void 0 ? void 0 : _a[cellIndex];
        var titleFromColumn;
        switch (firstCellInColumn.type) {
            case 'BUTTON':
                titleFromColumn = firstCellInColumn.buttonCaption;
                break;
            case 'CTA':
                titleFromColumn = firstCellInColumn.title;
                break;
            case undefined:
                titleFromColumn = firstCellInColumn.text || '';
                break;
        }
        return titleFromColumn;
    };
    var getModalTitleFromRowHeader = function (currentRow) {
        var firstCellInRow = currentRow === null || currentRow === void 0 ? void 0 : currentRow[0];
        var titleFromRow = (isBaseCell(firstCellInRow) && firstCellInRow.text) || '';
        return titleFromRow;
    };
    var isVisibleColumn = useCallback(function (cellIndex) { return !hideColumns.includes(cellIndex); }, [hideColumns]);
    return (jsxs("table", { className: classNames(className, 'w100', styles.table), width: width, children: [jsx("caption", { className: "sr-only", children: title }), headerRow && (jsx("thead", { className: hideHeader ? 'sr-only' : '', children: jsx("tr", { children: headerRow.map(function (tableCellData, cellIndex) {
                        var isFirstCellInRow = cellIndex === 0;
                        var cellReplacementData = (tableCellData.cellId &&
                            (cellReplacements === null || cellReplacements === void 0 ? void 0 : cellReplacements[tableCellData.cellId])) ||
                            {};
                        var cellProps = __assign(__assign(__assign({}, tableCellData), cellReplacementData), {
                            openModal: openModal,
                            modalTitle: (isBaseCell(tableCellData) && tableCellData.text) ||
                                getModalTitleFromColumnHeader(cellIndex),
                            align: isFirstCellInRow ? 'left' : 'center',
                        });
                        return (isVisibleColumn(cellIndex) && (jsx(TableCell, __assign({ isBelowDesktop: isBelowDesktop, isHeader: true, isFirstCellInRow: isFirstCellInRow, isTopLeftCell: isFirstCellInRow }, cellProps, { imageComponent: imageComponent }), cellIndex)));
                    }) }) })), jsx("tbody", { children: tableCellRows.map(function (row, rowIndex) {
                    return rowIndex > 0 && (jsx("tr", { className: styles.tr, children: row.map(function (tableCellData, cellIndex) {
                            var key = "".concat(rowIndex, "-").concat(cellIndex);
                            var isFirstCellInRow = cellIndex === 0;
                            var titleFromRow = getModalTitleFromRowHeader(row);
                            var cellReplacementData = (tableCellData.cellId &&
                                (cellReplacements === null || cellReplacements === void 0 ? void 0 : cellReplacements[tableCellData.cellId])) ||
                                {};
                            var cellProps = __assign(__assign(__assign({}, tableCellData), cellReplacementData), {
                                openModal: openModal,
                                modalTitle: (tableCellData === null || tableCellData === void 0 ? void 0 : tableCellData.modalTitle) || titleFromRow,
                                align: isFirstCellInRow ? 'left' : 'center',
                            });
                            return (!hideColumns.includes(cellIndex) && (jsx(TableCell, __assign({ isBelowDesktop: isBelowDesktop, isFirstCellInRow: isFirstCellInRow }, cellProps, { imageComponent: imageComponent }), key)));
                        }) }, rowIndex));
                }) })] }));
};

export { TableSection as T, isBaseCell as i, useMediaQuery as u };
//# sourceMappingURL=TableSection-ebace923.js.map
